import { SearchOutlined } from '@ant-design/icons';
import React, { useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import type { InputRef } from 'antd';
import { Button, Input, Space, Table, Tooltip, message } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import { RedoOutlined } from '@ant-design/icons';
import PoupVideo from './PopupVideo';
import Captcha from './Capcha';
import { fetchAPI } from '../../utils/use.api';
import { removeImage } from '../../utils/firebase/uploadImage';

interface DataType {
    _id: string;
    name: string;
    yearofbirth: number;
    hometown: string;
    reach: number;
    engagement: number;
    image_url: string;
    path_image_firebase: string;
    video_url: string;
}

type DataIndex = keyof DataType;

export default function TableAdmin(
    {
        data,
        selectedRowKey,
        setSelectedRowKey,
        fetchNewData
    }
        :
        {
            data: DataType[],
            selectedRowKey: any,
            setSelectedRowKey: any,
            fetchNewData: any
        }) {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                // @ts-ignore
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
            // console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
            setSelectedRowKey(selectedRows[0]);
        },
        columnTitle: (
            <Tooltip title="Bỏ chọn hàng hiện tại.">
                <RedoOutlined
                    className="icon-reset-rad-btn"
                    onClick={() => {
                        setSelectedRowKey(null);
                    }}
                />
            </Tooltip>
        ),
    };

    const columns = [
        {
            title: 'STT',
            render: (text: any, record: any, index: any) => index + 1,
            width: '5%',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            ...getColumnSearchProps('name'),
            width: '20%',
        },
        {
            title: 'Year of birth',
            dataIndex: 'yearofbirth',
            key: 'yearofbirth',
            width: '10%',
        },
        {
            title: 'Hometown',
            dataIndex: 'hometown',
            key: 'hometown',
            width: '10%',
        },
        {
            title: 'Reach',
            dataIndex: 'reach',
            key: 'reach',
            width: '10%',
        },
        {
            title: 'Engagement',
            dataIndex: 'engagement',
            key: 'engagement',
            width: '10%',
        },
        {
            title: 'Image',
            dataIndex: 'image_url',
            key: 'image_url',
            width: '10%',
            render: (text: any) => <img src={text} alt="img" style={{ width: '3rem' }} />
        },
        {
            title: 'Video',
            dataIndex: 'video_url',
            key: 'video_url',
            width: '10%',
            render: (text: any) => <PoupVideo id={text} />
        },
        {
            title: 'Capcha',
            dataIndex: 'capcha',
            key: 'capcha',
            width: '10%',
            render: (text: any) => <Captcha title='Xóa' callBack={() => {
                fetchAPI({
                    method: 'DELETE',
                    Headers: {
                        'AccessToken': localStorage.getItem('token'),
                    },
                    uri: '/posts/delete/' + selectedRowKey._id,
                })
                    .then((res: any) => {
                        if (res?.acknowledged && res?.deletedCount === 1) {
                            message.success('Xóa thành công');
                            removeImage(selectedRowKey.path_image_firebase);
                            fetchNewData();
                            setSelectedRowKey(null);
                        } else {
                            message.error('Xóa thất bại');
                        }
                    })
            }} />
        }
    ];

    return (
        <Table
            style={{ whiteSpace: 'nowrap' }}
            scroll={{ x: 1500 }}
            columns={columns}
            dataSource={data}
            rowSelection={{
                type: 'radio',
                selectedRowKeys: [selectedRowKey?._id],
                ...rowSelection,
            }}
            onRow={(record) => {
                return {
                    onClick: () => {
                        setSelectedRowKey(record);
                    },
                };
            }}
            rowKey={(record) => record._id}
            pagination={{ pageSize: 10 }}
            size='small'
        />
    )
};
